/**
 * SystemMessageModal — first-login banner for active system messages.
 * Auto-opens when /api/system-messages/active returns a non-null payload.
 * "Mark seen" closes it and prevents re-display.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Megaphone, AlertTriangle, Info, X } from 'lucide-react';
import { API_URL } from '../utils/api';

const SEVERITY_META = {
  info:     { color: '#0EA5E9', bg: '#EFF6FF', icon: Info,           label: 'Tájékoztatás' },
  warning:  { color: '#D97706', bg: '#FFFBEB', icon: AlertTriangle,  label: 'Figyelmeztetés' },
  critical: { color: '#DC2626', bg: '#FEF2F2', icon: AlertTriangle,  label: 'Sürgős' },
};

export default function SystemMessageModal() {
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/system-messages/active`, { withCredentials: true });
        if (!cancel && r.data) setMsg(r.data);
      } catch { /* unauth or no msg */ }
    })();
    return () => { cancel = true; };
  }, []);

  const close = async () => {
    try {
      await axios.post(`${API_URL}/api/system-messages/mark-seen`,
        { message_id: msg.id }, { withCredentials: true });
    } catch { /* ignore */ }
    setMsg(null);
  };

  if (!msg) return null;
  const meta = SEVERITY_META[msg.severity] || SEVERITY_META.info;
  const Icon = meta.icon;

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
         data-testid="sysmsg-modal" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
           style={{ backgroundColor: 'white' }}>
        <div className="px-6 py-4 flex items-start gap-3" style={{ background: meta.bg }}>
          <Icon size={22} style={{ color: meta.color }} />
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>
              {meta.label}
            </p>
            <h2 className="text-lg font-bold mt-0.5" style={{ color: '#0F172A' }}>{msg.title}</h2>
          </div>
          <button onClick={close} data-testid="sysmsg-close"
            className="p-1 hover:bg-white/40 rounded">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm whitespace-pre-wrap" style={{ color: '#334155' }}>{msg.body}</p>
        </div>
        <div className="px-6 py-3 flex justify-end" style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
          <button onClick={close} data-testid="sysmsg-ok"
            className="px-5 py-2 rounded-lg text-sm font-semibold"
            style={{ background: meta.color, color: 'white' }}>
            Megértettem
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
